
import { windowatch } from '../../../scripts/lib/windowatch';

const playerAPIUrl = 'https://www.youtube.com/iframe_api';
let playerAPILoadListeners = [];
let playerAPILoading = false;

export default class VideoOverlay {

  constructor (videoId) {

    this.$el = null;
    this.visible = false;
    this.videoId = videoId;
    this.player = null;
    this.playerReady = false;
    this.resizeHandler = this.onLayout.bind(this);

    // build overlay
    this.$el = document.createElement('div');
    this.$el.className = 'video-overlay';

    const $backdrop = document.createElement('div');
    $backdrop.className = 'video-overlay__backdrop';
    this.$el.appendChild($backdrop);

    const $content = document.createElement('div');
    $content.className = 'video-overlay__content';
    this.$el.appendChild($content);

    this.$player = document.createElement('div');
    this.$player.className = 'video-overlay__player';
    $content.appendChild(this.$player);

    // create button from button template
    const $buttonTemplate = document.querySelector('.template-btn-close');
    if ($buttonTemplate) {
      const $button = document.createElement('div');
      $button.className = 'video-overlay__btn-close';
      $button.innerHTML = $buttonTemplate.innerHTML;
      $button.querySelector('.button')
        .addEventListener('click', this.close.bind(this));
      $content.appendChild($button);
    }

    // load player api
    VideoOverlay.loadPlayerAPI(this.onPlayerAPIReady.bind(this));
  }

  onPlayerAPIReady (playerAPI) {

    if (!playerAPI) {
      // cancel here
      return;
    }

    // calculate player height such that it is full width with ratio 16:9
    const playerWidth = window.innerWidth;
    const playerHeight = playerWidth / 16 * 9;

    // use player API to create the player in DOM
    this.player = new playerAPI.Player(this.$player, {
      width: `${playerWidth}px`,
      height: `${playerHeight}px`,
      videoId: this.videoId,
      host: 'https://www.youtube-nocookie.com',
      playerVars: {
        autoplay: 1,
        modestbranding: 1,
        rel: 0
      },
      events: {
        onReady: this.onPlayerReady.bind(this),
        onStateChange: this.onPlayerStateChange.bind(this)
      }
    });
  }

  /**
   * Shows the video overlay.
   * @param {HTMLElement} $source Image component to transition from
   * @return {VideoOverlay} Fluent interface
   */
  show ($source) {
    return this.setVisible(true);
  }

  /**
   * Closes the video overlay.
   * @return {VideoOverlay} Fluent interface
   */
  close () {
    return this.setVisible(false);
  }

  /**
   * Sets overlay visibility.
   * @param {boolean} visible Wether to make the overlay visible or not
   * @return {VideoOverlay} Fluent interface
   */
  setVisible (visible) {

    if (this.visible !== visible) {
      this.visible = visible;

      if (visible) {

        // add self to dom
        document.body.appendChild(this.$el);

        // add resize listener
        windowatch.addResizeListener(this.resizeHandler);

      } else {

        // remove resize listener
        windowatch.removeResizeListener(this.resizeHandler);

        // stop playback
        if (this.player && this.playerReady) {
          this.player.stopVideo();
        }
      }

      // trigger layout
      this.$el.getBoundingClientRect();

      // listen to the transition end event; add a short delay to let the
      // transition finish without cutting off frames at the end
      this.$el.ontransitionend = () =>
        setTimeout(this.onVisibilityChange.bind(this, visible), 100);

      // trigger transition
      if (visible) {
        this.$el.classList.add('video-overlay--visible');
      } else {
        this.$el.classList.remove('video-overlay--visible');
      }
    }

    return this;
  }

  /**
   * Triggered when finishing the visibility change transition.
   */
  onVisibilityChange (visible) {

    // remove listener to prevent multiple event calls
    this.$el.ontransitionend = null;

    if (visible) {

      // Video will start automatically

    } else {

      // invalidate player
      if (this.player) {
        if (this.playerReady) {
          this.player.destroy();
        }

        this.player = null;
      }

      // remove self from dom
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    }
  }

  /**
   * Triggered on window resize.
   * @param {number} width Window width
   * @param {number} height Window height
   */
  onLayout (width, height) {

    // resize player
    if (this.player) {
      this.player.setSize(width, width / 16 * 9);
    }
  }

  /**
   * Triggered when YouTube player is ready.
   * @param {Event} evt
   */
  onPlayerReady (evt) {
    this.playerReady = true;
  }

  /**
   * Triggered when player state changes.
   * @param {Event} evt
   */
  onPlayerStateChange (evt) {

    switch (evt.data) {
      case 0:
        // ended, close the video overlay
        this.close();
        break;
    }
  }

  /**
   * Lazily loads the player API async and invokes the callback when finished.
   * @param {function} callback Sends the player API root object as first
   * parameter or false, when loading failed.
   */
  static loadPlayerAPI (callback) {

    if (window.YT !== undefined) {
      // player API is ready, run callback immediately
      callback(window.YT);

      return;
    }

    // add callback to load listeners
    playerAPILoadListeners.push(callback);

    if (!playerAPILoading) {

      // set loading flag
      playerAPILoading = true;

      // listen to the YouTube API ready callback
      if (window.onYouTubeIframeAPIReady === undefined) {
        window.onYouTubeIframeAPIReady = () => {
          // inform listeners that YouTube API is ready
          playerAPILoading = false;
          playerAPILoadListeners.forEach(callback => callback(window.YT));
          playerAPILoadListeners = [];
        };
      }

      const $script = document.createElement('script');
      $script.src = playerAPIUrl;
      $script.onerror = () => {
        // inform listeners that YouTube API loading failed. Maybe offline?
        playerAPILoading = false;
        playerAPILoadListeners.forEach(callback => callback(false));
        playerAPILoadListeners = [];
      };

      // inject script into dom to trigger loading
      const $firstScript = document.querySelector('script');
      $firstScript.parentNode.insertBefore($script, $firstScript);
    }
  }
}
